"use client";

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { useAuth } from "./AuthContext";
import { useCollections } from "./CollectionsContext";

interface SavedProductsContextType {
  savedProductIds: Set<string>;
  loading: boolean;
  isSaved: (productId: string) => boolean;
  toggleSaved: (productId: string) => Promise<void>;
  registerProduct: (productId: string) => void;
  fetchSavedProducts: () => Promise<void>;
}

const SavedProductsContext = createContext<SavedProductsContextType | undefined>(undefined);

export function SavedProductsProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const {
    collections,
    addProductToCollection,
    removeProductFromCollection,
    loading: collectionsLoading,
    fetchCollections,
  } = useCollections();

  const [savedProductIds, setSavedProductIds] = useState<Set<string>>(new Set());
  const registeredProducts = useRef<Set<string>>(new Set());

  // Recompute savedProductIds whenever collections change
  // Saved set reflects ONLY the default "All Saves" collection
  useEffect(() => {
    const defaultCollection = collections.find((c) => c.isDefault);
    const ids = new Set<string>(defaultCollection?.productIds || []);
    setSavedProductIds(ids);
  }, [collections]);

  const fetchSavedProducts = useCallback(async () => {
    // Ensure we have latest collections
    await fetchCollections();
  }, [fetchCollections]);

  const registerProduct = useCallback((productId: string) => {
    registeredProducts.current.add(productId);
  }, []);

  const isSaved = useCallback(
    (productId: string) => {
      return savedProductIds.has(productId);
    },
    [savedProductIds]
  );

  const toggleSaved = useCallback(
    async (productId: string) => {
      // Skip for logged-out or anonymous users - they don't have backend user records
      if (!user || !isAuthenticated) return;
      // Use default or first collection as target
      const targetCollection = collections.find((c) => c.isDefault) || collections[0];
      if (!targetCollection) return;
      const currentlySaved = targetCollection.productIds.includes(productId);

      if (currentlySaved) {
        await removeProductFromCollection(targetCollection.id, productId);
      } else {
        await addProductToCollection(targetCollection.id, productId);
      }
      // Refresh collections
      await fetchCollections();
    },
    [
      user,
      isAuthenticated,
      collections,
      addProductToCollection,
      removeProductFromCollection,
      fetchCollections,
    ]
  );

  const loading = collectionsLoading;

  return (
    <SavedProductsContext.Provider
      value={{
        savedProductIds,
        loading,
        isSaved,
        toggleSaved,
        registerProduct,
        fetchSavedProducts,
      }}
    >
      {children}
    </SavedProductsContext.Provider>
  );
}

export const useSavedProducts = () => {
  const context = useContext(SavedProductsContext);
  if (!context) {
    throw new Error("useSavedProducts must be used within SavedProductsProvider");
  }
  return context;
};
